/**
 * AUTO-GENERATED — DO NOT EDIT.
 * Source: sdk.manifest.yml + openapi.json.
 * Regenerate with: pnpm gen
 */


import { type QueryFunctionContext } from '@tanstack/react-query';

import { recordSnapshotLastSeq } from '../../nats/snapshot-seq-store.js';

import { fetchWithHttpTelemetry } from '../../transport/http-request.js';

import { throwHttpError } from '../../core/http-error.js';

import type { GetMeRow, GetSessionsRow } from '../openapi-types.gen.js';

import type { EntityKeyFactory, HookSpec } from '../../types/HookSpec.js';
import type { JsonSerialisableArgs } from '../../types/JsonSerialisableArgs.js';


// ─── useGetMe ────────────────────────────────────────────────────────────

export interface useGetMeArgs { readonly kind?: string; readonly fields?: readonly ('id' | 'email' | 'emailVerified' | 'phoneNumber' | 'displayName' | 'handle' | 'avatarUrl' | 'avatarNsfw' | 'avatarPreferences' | 'heroUrl' | 'heroNsfw' | 'bio' | 'country' | 'locationSource' | 'totalVolume' | 'totalTrades' | 'realizedPnl' | 'unrealizedPnl' | 'totalPnl' | 'marketsTraded' | 'interestTopics' | 'interestPlatforms' | 'createdAt' | 'turnkeySubOrgId' | 'giftsDisabled' | 'giftIndexingEnabled' | 'onboardingCompleted' | 'wallets' | 'connectedProviders')[]; }


async function _useGetMeQueryFn(
  args: useGetMeArgs,
  ctx: QueryFunctionContext,
): Promise<GetMeRow | null> {
  const url = '/v1/me';
  const params = new URLSearchParams();
  if (args.fields !== undefined) params.set('fields', args.fields.join(','));
  if (args.kind !== undefined) params.set('kind', String(args.kind));
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetMe',
    routeTemplate: '/v1/me',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  // anyOf disambiguation: the data is one of N branches; the branch
  // containing the named field IS the row shape (do not unwrap further —
  // the row's own fields are the manifest's `fields:` list).
  return data as GetMeRow;
}


const _useGetMeFactory = Object.assign(
  (args: useGetMeArgs) => ({
    queryKey: ['me', 'detail', { kind: args.kind ?? null }, args.fields ?? []] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetMeQueryFn(args, ctx),
    staleTime: 2000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetMe',
      viewerSensitive: true,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['me', 'detail'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetMeArgs, GetMeRow>;


export const useGetMeSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/me',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { detail: _useGetMeFactory },
  parseInput: (raw: unknown): useGetMeArgs => raw as useGetMeArgs,
  parseResponse: (raw: unknown): GetMeRow => raw as GetMeRow,
  specName: 'useGetMe' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: true as const,
  nonpersistent: true as const,
  privateAccountEntity: true as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetMeRow>;


// ─── useGetSessions ────────────────────────────────────────────────────────────

export interface useGetSessionsArgs { readonly kind?: string; readonly fields?: readonly ('sessions')[]; }


async function _useGetSessionsQueryFn(
  args: useGetSessionsArgs,
  ctx: QueryFunctionContext,
): Promise<GetSessionsRow | null> {
  const url = '/v1/me';
  const params = new URLSearchParams();
  if (args.fields !== undefined) params.set('fields', args.fields.join(','));
  if (args.kind !== undefined) params.set('kind', String(args.kind));
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetSessions',
    routeTemplate: '/v1/me',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  // anyOf disambiguation: the data is one of N branches; the branch
  // containing the named field IS the row shape (do not unwrap further —
  // the row's own fields are the manifest's `fields:` list).
  return data as GetSessionsRow;
}


const _useGetSessionsFactory = Object.assign(
  (args: useGetSessionsArgs) => ({
    queryKey: ['me', 'detail', { kind: args.kind ?? null }, args.fields ?? []] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetSessionsQueryFn(args, ctx),
    staleTime: 2000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetSessions',
      viewerSensitive: true,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['me', 'detail'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetSessionsArgs, GetSessionsRow>;


export const useGetSessionsSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/me',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { detail: _useGetSessionsFactory },
  parseInput: (raw: unknown): useGetSessionsArgs => raw as useGetSessionsArgs,
  parseResponse: (raw: unknown): GetSessionsRow => raw as GetSessionsRow,
  specName: 'useGetSessions' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: true as const,
  nonpersistent: true as const,
  privateAccountEntity: true as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetSessionsRow>;
